import { Button } from '@mui/material';
import { isEmpty, Modal } from '@vxui/components';
import { FormikErrors, FormikProvider, useFormik } from 'formik';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ResetPassword, User } from '../../models';
import { useResetPasswordMutation } from '../../services';
import { PasswordForm } from './PasswordForm';
import { maxPasswordLength, minPasswordLength } from './PasswordForm.helpers';

type ResetPasswordModalProps = {
  user: User;
  onClose: () => void;
};

export const resetPasswordModalId = 'resetPasswordModal';

export const ResetPasswordModal: FC<ResetPasswordModalProps> = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [resetPassword] = useResetPasswordMutation();

  const formik = useFormik<ResetPassword>({
    initialValues: { userID: user.userID, login: user.login, newPassword: '', confirmedNewPassword: '' },
    onSubmit: async ({ confirmedNewPassword: _, login: __, ...newPass }) => {
      try {
        await resetPassword({ ...newPass }).unwrap();
        onClose();
      } catch (e) {
        console.error(`Unable to reset password for user ${user.userID}`, e);
      }
    },
    validateOnBlur: true,
    validate: values => {
      const error = (fieldName: string, errorType: string, values = {}) =>
        t(`auth.forms.password.fields.${fieldName}.errors.${errorType}`, values);

      const errors: FormikErrors<ResetPassword> = {};

      // new
      if (isEmpty(values.newPassword)) errors.newPassword = error('newPassword', 'empty');
      else if (values.newPassword.trim().length < minPasswordLength)
        errors.newPassword = error('newPassword', 'tooShort', { min: minPasswordLength });
      else if (values.newPassword.trim().length > maxPasswordLength)
        errors.newPassword = error('newPassword', 'tooLong', { max: maxPasswordLength });

      // confirmed
      if (values.newPassword !== values.confirmedNewPassword)
        errors.confirmedNewPassword = error('confirmedNewPassword', 'dontMatch');

      return errors;
    },
  });

  const submit = useCallback(() => formik.submitForm(), [formik]);

  return (
    <Modal
      open
      title={t('auth.modal.resetPassword.title')}
      actions={
        <>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {t('button.close')}
          </Button>
          <Button onClick={submit} color="secondary" variant="contained">
            {t('button.save')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <PasswordForm isReset />
      </FormikProvider>
    </Modal>
  );
};
